import { Component, OnInit, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { ListItem } from '../models/ListItem';
import { ScheduleParameters } from '../models/ScheduleParameters';
import { LocationType } from '../models/Enums';
import { ListDataService } from '../services/list-data.service';
import { LocDefault } from '../models/LocDefault';
import { UserDataService } from '../services/user-data.service';
import { UserSettingsService } from '../services/user-settings.service';
import { TruckerSummaryComponent } from '../trucker-summary/trucker-summary.component';

@Component({
    selector: 'scheduling',
    templateUrl: 'scheduling.component.html',
    styles: [],
    standalone: false
})
export class SchedulingComponent implements OnInit {

    constructor(private _listDataService: ListDataService, private _userDataService: UserDataService, private _userSettingsService: UserSettingsService) { }

    @ViewChild('truckerSummary') truckerSummary!: TruckerSummaryComponent;

    scheduleParms: ScheduleParameters = new ScheduleParameters();
    refreshParameters: ScheduleParameters = new ScheduleParameters();
    refreshSchedulesData: boolean = false;
    refreshStagingGrid: boolean = false;
    refreshSchedulesGrid: boolean = false;
    refreshingStaging: boolean = false;
    refreshingSchedules: boolean = false;
    invalidDateRange: boolean = false;

    locList: ListItem[] = [];
    planPeriods: ListItem[] = [];

    errorMessage: string = '';
    applyUserDefaults: boolean = true;

    tabClick(tab: string): void {
        switch (tab) {
            case 'staging':
                this.refreshStagingGrid = !this.refreshStagingGrid;
                break;
            case 'schedule':
                this.refreshSchedulesGrid = !this.refreshSchedulesGrid;
                break;
            case 'truckerSummary':
                this.truckerSummary.refreshData();
                break;
        }
    }

    getPlanPeriods(): void {
        if (this.scheduleParms.locCode.length === 0) {
            return;
        }

        this._listDataService.getPlanPeriods(this.scheduleParms.locCode).subscribe({
            next: data => {
                this.planPeriods = data;
                if (this.planPeriods.length > 0) {
                    const planPeriodExists = this.planPeriods.some(item => item.value === this.scheduleParms.planPeriod);
                    if (!planPeriodExists) {
                        this.scheduleParms.planPeriod = this.planPeriods[0].value;
                    }
                    this.getPeriodDates(true);
                }
            },
            error: err => this.errorMessage = err
        });
    }

    trackPlanPeriod(index: number, item: ListItem) {
        return item.value;
    }

    getPeriodDates(refreshData: boolean): void {
        this._listDataService.getPeriodDates(this.scheduleParms.planPeriod).subscribe({
            next: data => {
                this.scheduleParms.periodStartDate = data.startDate;
                this.scheduleParms.periodEndDate = data.endDate;
                this.scheduleParms.shipThroughDate = data.shipThroughDate;
                if (refreshData && this.scheduleParms.locCode.length > 0) {
                    this.refreshScheduleData(true);
                }
            },
            error: err => this.errorMessage = err
        });
    }

    locCodeChange() {
        this.getLocDefaultValues();
    }

    planPeriodChange() {
        if (this.scheduleParms.planPeriod !== 'Custom') {
            this.getPeriodDates(false);
        }
    }

    refreshingData(): boolean {
        return this.refreshingStaging || this.refreshingSchedules;
    }

    refreshScheduleData(loadDefaultFilters: boolean) {
        if (!this.validateDates()) {
            return;
        }

        this.refreshingStaging = true;
        this.refreshingSchedules = true;
        this.refreshParameters = {
            ...this.scheduleParms,
            loadDefaultFilters: loadDefaultFilters
        };
        this._userSettingsService.setCurrentLocCode(this.scheduleParms.locCode);
        sessionStorage.setItem("periodStartDate", this.scheduleParms.periodStartDate);
    }

    validateDates(): boolean {
        this.invalidDateRange = false;
        const startDate = new Date(this.scheduleParms.periodStartDate);
        const endDate = new Date(this.scheduleParms.periodEndDate);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) ||
            startDate.getTime() > endDate.getTime()) {
            this.invalidDateRange = true;
            return false;
        }
        return true;
    }

    stagingRefreshComplete(complete: boolean) {
        this.refreshingStaging = false;
    }

    schedulesRefreshComplete(complete: boolean) {
        this.refreshingSchedules = false;
    }

    schedulesChanged(change: boolean) {
        this.refreshSchedulesData = !this.refreshSchedulesData;
    }

    getLocDefaultValues() {
        if (this.scheduleParms.locCode.length === 0) {
            return;
        }

        //switch back to the defaults to start - these will be the values if there are no Loc defaults
        this.scheduleParms.locationType = LocationType.Destination;
        this.scheduleParms.planPeriod = 'WeekCurrent';

        const keys = ['DefaultPlanningPerspective', 'DefaultPlanningWindow'];
        this._listDataService.getLocDefaults(this.scheduleParms.locCode, keys).subscribe({
            next: data => {
                const planByDefault = data.find(x => x.key === 'DefaultPlanningPerspective');
                if (planByDefault !== undefined && planByDefault.value.length > 0) {
                    this.scheduleParms.locationType = planByDefault.value === 'Origin' ? LocationType.Origin : LocationType.Destination;
                }
                const planPeriodDefault = data.find(x => x.key === 'DefaultPlanningWindow');
                if (planPeriodDefault !== undefined && planPeriodDefault.value.length > 0) {
                    this.scheduleParms.planPeriod = planPeriodDefault.value;
                }

                this.getPlanPeriods();
            },
            error: err => { this.errorMessage = err; }
        });
    }

    ngOnInit(): void {
        const sessionStoreLocCode = sessionStorage.getItem("locCode");
        this.scheduleParms.locationType = LocationType.Destination;
        this.scheduleParms.planPeriod = 'WeekCurrent';

        forkJoin({
            locs: this._listDataService.getListItems("loc"),
            settings: this._userDataService.getUserSettings()
        }).subscribe({
            next: ({ locs, settings }) => {
                this.locList = locs;
                this.scheduleParms.locCode =
                    (sessionStoreLocCode && sessionStoreLocCode !== "")
                        ? sessionStoreLocCode!
                        : settings.defaultLocCode;
                this.loadToolbarDefaults();
            },
            error: err => this.errorMessage = err
        });
    }

    private loadToolbarDefaults(): void {
        if (this.scheduleParms.locCode.length === 0) {
            return;
        }

        this._listDataService.getSchedulingToolbarDefaults(this.scheduleParms.locCode)
            .subscribe({
                next: defaults => {
                    this.scheduleParms.locationType =
                        defaults.locationType === 'Origin'
                            ? LocationType.Origin : LocationType.Destination;
                    this.planPeriods = defaults.planPeriods;
                    this.scheduleParms.planPeriod = defaults.planPeriod;
                    this.scheduleParms.periodStartDate = defaults.periodDates.startDate;
                    this.scheduleParms.periodEndDate = defaults.periodDates.endDate;
                    this.scheduleParms.shipThroughDate = defaults.periodDates.shipThroughDate;
                    this.refreshScheduleData(true);
                },
                error: err => this.errorMessage = err
            });
    }
}
